import React, { useEffect, useState } from 'react';

// Facilitator view for the Clue activity. Gated by CLUE_HOST_KEY (the same
// static-bearer-token convention as the trading/workplace sim's admin
// routes) instead of a password+cookie login — enter the key once, it's
// kept in sessionStorage for the rest of the tab's life.

const API_BASE = 'https://forteindiedev.com/api/clue';
const POLL_MS = 4000;

function useHostKey() {
    const [key, setKey] = useState(() => sessionStorage.getItem('clue_host_key') || '');
    function save(k) {
        sessionStorage.setItem('clue_host_key', k);
        setKey(k);
    }
    return [key, save];
}

async function hostFetch(key, path, options = {}) {
    const res = await fetch(`${API_BASE}/host${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${key}`,
            ...(options.headers || {}),
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `request failed: ${res.status}`);
    return data;
}

const btnStyle = {
    padding: '8px 14px',
    borderRadius: 6,
    border: '1px solid #444',
    background: '#2a2a2a',
    color: '#eee',
    cursor: 'pointer',
    marginRight: 8,
    marginBottom: 8,
};

export default function ClueHost() {
    const [key, saveKey] = useHostKey();
    const [keyInput, setKeyInput] = useState('');
    const [snapshot, setSnapshot] = useState(null);
    const [error, setError] = useState(null);
    const [playerPhone, setPlayerPhone] = useState('');
    const [playerName, setPlayerName] = useState('');

    async function refresh() {
        if (!key) return;
        try {
            const data = await hostFetch(key, '/snapshot');
            setSnapshot(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    }

    useEffect(() => {
        if (!key) return;
        refresh();
        const id = setInterval(refresh, POLL_MS);
        return () => clearInterval(id);
    }, [key]);

    async function act(path, options, successMsg) {
        try {
            await hostFetch(key, path, options);
            await refresh();
        } catch (err) {
            setError(err.message);
        }
    }

    if (!key) {
        return (
            <div style={{ padding: 40, color: '#eee', background: '#111', minHeight: '100vh' }}>
                <h2>Clue host key</h2>
                <input
                    type="password"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="CLUE_HOST_KEY"
                    style={{ padding: 8, width: 320, marginRight: 8 }}
                />
                <button style={btnStyle} onClick={() => saveKey(keyInput)}>Enter</button>
            </div>
        );
    }

    return (
        <div style={{ background: '#111', color: '#eee', minHeight: '100vh', padding: '24px 32px', fontFamily: 'system-ui, sans-serif' }}>
            <h1>Clue — Facilitator</h1>
            {error && <div style={{ color: '#ff5252', marginBottom: 16 }}>Error: {error}</div>}
            <button style={btnStyle} onClick={() => saveKey('')}>Log out</button>

            {!snapshot ? (
                <div>Loading...</div>
            ) : (
                <>
                    <h2>Game state</h2>
                    <div style={{ marginBottom: 12 }}>
                        Phase: <strong>{snapshot.phase}</strong> · Round: {snapshot.round_index}
                        {snapshot.round && <> ({snapshot.round.name})</>} · Outbound paused: {String(snapshot.paused_outbound)}
                    </div>
                    <div>
                        <button style={btnStyle} onClick={() => act('/advance', { method: 'POST' })}>Advance phase →</button>
                        <button style={btnStyle} onClick={() => act('/reopen', { method: 'POST' })}>Reopen round</button>
                        <button style={btnStyle} onClick={() => act('/pause-outbound', { method: 'POST' })}>Pause outbound</button>
                        <button style={btnStyle} onClick={() => act('/resume-outbound', { method: 'POST' })}>Resume outbound</button>
                        <button
                            style={{ ...btnStyle, borderColor: '#b71c1c' }}
                            onClick={() => {
                                if (window.confirm('Reset ALL game state? This wipes calls/beats/events/accusations back to lobby.')) {
                                    act('/reset', { method: 'POST' });
                                }
                            }}
                        >
                            Reset game
                        </button>
                    </div>

                    <h2 style={{ marginTop: 28 }}>Killer (facilitator only)</h2>
                    <div style={{ color: '#ff8a80', fontWeight: 700 }}>{snapshot.killer_id}</div>

                    <h2 style={{ marginTop: 28 }}>Cast</h2>
                    {snapshot.cast.map((c) => (
                        <div key={c.id} style={{ marginBottom: 8 }}>
                            <strong>{c.display_name}</strong> — {c.status}{' '}
                            <button
                                style={{ ...btnStyle, padding: '4px 10px' }}
                                onClick={() => act(`/characters/${c.id}/mute`, {
                                    method: 'POST',
                                    body: JSON.stringify({ muted: c.status !== 'muted' }),
                                })}
                            >
                                {c.status === 'muted' ? 'Unmute' : 'Mute'}
                            </button>
                        </div>
                    ))}

                    <h2 style={{ marginTop: 28 }}>Register player</h2>
                    <input
                        placeholder="phone (+1555...)"
                        value={playerPhone}
                        onChange={(e) => setPlayerPhone(e.target.value)}
                        style={{ padding: 8, marginRight: 8 }}
                    />
                    <input
                        placeholder="display name"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        style={{ padding: 8, marginRight: 8 }}
                    />
                    <button
                        style={btnStyle}
                        onClick={() => act('/players', {
                            method: 'POST',
                            body: JSON.stringify({ phone: playerPhone, display_name: playerName }),
                        })}
                    >
                        Register
                    </button>

                    <h2 style={{ marginTop: 28 }}>Beats</h2>
                    <button style={btnStyle} onClick={() => act('/beats/suggest', { method: 'POST' })}>Suggest next beat</button>
                    {snapshot.beats.map((b) => (
                        <div key={b.id} style={{ border: '1px solid #333', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                            <div>
                                <strong>{b.kind}</strong> · {b.status} · from {b.from_character_id}
                                {b.to_character_id && <> → {b.to_character_id}</>}
                                {b.to_phone && <> → {b.to_phone}</>}
                            </div>
                            <div style={{ color: '#ccc', fontSize: 13, margin: '4px 0' }}>{b.public_line}</div>
                            {b.kind === 'rumor' && b.status === 'staged' && (
                                <button
                                    style={{ ...btnStyle, padding: '4px 10px' }}
                                    onClick={async () => {
                                        try {
                                            const result = await hostFetch(key, `/beats/${b.id}/place`, { method: 'POST' });
                                            window.alert(
                                                `Now dial ${result.dial.from_character} → ${result.dial.to_phone} in the platform.\n\n` +
                                                `Nothing to paste: the call links to this rumor automatically.`
                                            );
                                            refresh();
                                        } catch (err) {
                                            setError(err.message);
                                        }
                                    }}
                                >
                                    Place call
                                </button>
                            )}
                            {(b.status === 'staged' || b.status === 'calling') && (
                                <button
                                    style={{ ...btnStyle, padding: '4px 10px' }}
                                    onClick={() => act(`/beats/${b.id}/force-complete`, { method: 'POST' })}
                                >
                                    Force-complete
                                </button>
                            )}
                        </div>
                    ))}

                    <h2 style={{ marginTop: 28 }}>Open / recent calls</h2>
                    {snapshot.calls.map((c) => (
                        <div key={c.id} style={{ fontSize: 13, marginBottom: 6 }}>
                            #{c.id} {c.character_id} · {c.direction} · {c.closed_at ? 'closed' : 'OPEN'}
                            {!c.closed_at && (
                                <button
                                    style={{ ...btnStyle, padding: '2px 8px', marginLeft: 8 }}
                                    onClick={() => act(`/calls/${c.id}/clear`, { method: 'POST' })}
                                >
                                    Clear stuck call
                                </button>
                            )}
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}
